import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { AddressField } from "@/components/form/AddressField";
import { AddressPickHint } from "@/components/form/AddressPickHint";
import { AddressConfirmedHint } from "@/components/form/AddressConfirmedHint";
import { TimeField } from "@/components/form/TimeField";
import { WillCallToggle } from "@/components/form/WillCallToggle";
import { fullAddressFromPlace } from "@/lib/format";
import { WizardCard } from "./WizardCard";
import type { TripFormValues } from "./schema";
import type { WizardStepStatus } from "./WizardCard";

interface MultiLegSectionProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

export function MultiLegSection({
  index,
  status,
  onContinue,
  onEdit,
}: MultiLegSectionProps) {
  const { control, trigger } = useFormContext<TripFormValues>();
  const legs = useWatch({ control, name: "additionalLegs" }) ?? [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalLegs",
  });

  async function handleContinue() {
    const ok = await trigger("additionalLegs");
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    return (
      <WizardCard
        index={index}
        title="Additional legs"
        status="complete"
        summary={
          legs.length === 0
            ? "—"
            : `${legs.length} additional leg${legs.length === 1 ? "" : "s"}`
        }
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Additional legs"
      description="Add extra stops if this trip has more than two legs."
      status={status}
    >
      <div className="grid gap-3">
        {fields.map((legField, i) => (
          <LegCard key={legField.id} legIndex={i} onRemove={() => remove(i)} />
        ))}
        {fields.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-foreground/[0.02] px-3 py-3 text-sm text-muted-foreground">
            No legs yet — add one to continue.
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() =>
            append({
              from: "",
              to: "",
              fromCityZip: "",
              toCityZip: "",
              fromPlace: null,
              toPlace: null,
              time: "",
              notes: "",
              willCall: false,
            })
          }
        >
          <Plus className="h-4 w-4" /> Add Leg
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={fields.length === 0}
          className="min-w-[10rem]"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}

function LegCard({
  legIndex,
  onRemove,
}: {
  legIndex: number;
  onRemove: () => void;
}) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<TripFormValues>();

  const willCall = useWatch({
    control,
    name: `additionalLegs.${legIndex}.willCall` as const,
  });
  const fromPlace = useWatch({
    control,
    name: `additionalLegs.${legIndex}.fromPlace` as const,
  });
  const toPlace = useWatch({
    control,
    name: `additionalLegs.${legIndex}.toPlace` as const,
  });
  const fromAddress =
    useWatch({
      control,
      name: `additionalLegs.${legIndex}.from` as const,
    }) ?? "";
  const toAddress =
    useWatch({ control, name: `additionalLegs.${legIndex}.to` as const }) ?? "";

  const legErrors = errors.additionalLegs?.[legIndex];
  // "Location confirmed" when a stop has coordinates (Google dropdown / curated
  // location), an amber pick-from-dropdown nudge when it doesn't (the server
  // backfills as a safety net). The two states are mutually exclusive.
  const hasCoords = (
    p: { lat: number | null; lon: number | null } | null | undefined,
  ): boolean =>
    Boolean(
      p &&
        p.lat != null &&
        p.lon != null &&
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lon),
    );
  const hasFromAddress = fromAddress.trim().length > 0;
  const hasToAddress = toAddress.trim().length > 0;

  return (
    <article className="rounded-2xl border border-foreground/15 bg-foreground/[0.02] p-3 grid gap-3">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-bold">Leg {legIndex + 2}</h3>
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name={`additionalLegs.${legIndex}.willCall` as const}
            render={({ field }) => (
              <WillCallToggle checked={field.value ?? false} onCheckedChange={field.onChange} />
            )}
          />
          <Button variant="ghost" size="sm" type="button" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3">
        <div className="flex flex-col gap-1.5">
          <Field
            label="From address"
            required
            error={legErrors?.from?.message}
          >
            <Controller
              control={control}
              name={`additionalLegs.${legIndex}.from` as const}
              render={({ field }) => (
                <AddressField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onPlace={(place, cityZip) => {
                    // Full canonical address — see PickupSection / fullAddressFromPlace.
                    field.onChange(fullAddressFromPlace(place, field.value ?? ""));
                    setValue(`additionalLegs.${legIndex}.fromPlace` as const, place, {
                      shouldDirty: true,
                    });
                    if (cityZip)
                      setValue(`additionalLegs.${legIndex}.fromCityZip` as const, cityZip, {
                        shouldDirty: true,
                      });
                  }}
                  onClearPlace={() => {
                    setValue(`additionalLegs.${legIndex}.fromPlace` as const, null, {
                      shouldDirty: true,
                    });
                    setValue(`additionalLegs.${legIndex}.fromCityZip` as const, "", {
                      shouldDirty: true,
                    });
                  }}
                  placeholder="Pick-up for this leg"
                  maxLength={200}
                />
              )}
            />
          </Field>
          {hasFromAddress ? (
            hasCoords(fromPlace) ? <AddressConfirmedHint /> : <AddressPickHint />
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Field
            label="To address"
            required
            error={legErrors?.to?.message}
          >
            <Controller
              control={control}
              name={`additionalLegs.${legIndex}.to` as const}
              render={({ field }) => (
                <AddressField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onPlace={(place, cityZip) => {
                    // Full canonical address — see PickupSection / fullAddressFromPlace.
                    field.onChange(fullAddressFromPlace(place, field.value ?? ""));
                    setValue(`additionalLegs.${legIndex}.toPlace` as const, place, {
                      shouldDirty: true,
                    });
                    if (cityZip)
                      setValue(`additionalLegs.${legIndex}.toCityZip` as const, cityZip, {
                        shouldDirty: true,
                      });
                  }}
                  onClearPlace={() => {
                    setValue(`additionalLegs.${legIndex}.toPlace` as const, null, {
                      shouldDirty: true,
                    });
                    setValue(`additionalLegs.${legIndex}.toCityZip` as const, "", {
                      shouldDirty: true,
                    });
                  }}
                  placeholder="Destination for this leg"
                  maxLength={200}
                />
              )}
            />
          </Field>
          {hasToAddress ? (
            hasCoords(toPlace) ? <AddressConfirmedHint /> : <AddressPickHint />
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {!willCall ? (
          <Field label="Time" required error={legErrors?.time?.message}>
            <Controller
              control={control}
              name={`additionalLegs.${legIndex}.time` as const}
              render={({ field, fieldState }) => (
                <TimeField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  invalid={Boolean(fieldState.error)}
                />
              )}
            />
          </Field>
        ) : null}
        <Field label="Notes (optional)">
          <Input
            placeholder="Any details for this leg"
            maxLength={500}
            clearable
            {...register(`additionalLegs.${legIndex}.notes` as const)}
          />
        </Field>
      </div>
    </article>
  );
}
